/**
 * 付费墙 Modal
 *
 * 各业务在后端返回 PAYWALL_REQUIRED / QUOTA_EXHAUSTED 时打开。
 * 按 PaywallConfig 读取文案、CTA、A/B 变体，选择 CTA 后跳转下单 → 易支付 payUrl。
 */
import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { PaywallConfigView, PaywallCTA, PaywallVariant } from '@guanwei/shared';
import { Button } from '@/components/ui/Button';
import { createOrder, submitPayment } from '@/lib/business.api';
import { useAuthStore } from '@/lib/auth.store';

interface ReportPaywallProps {
  open: boolean;
  scene: string;
  config: PaywallConfigView | null;
  /** 用于附加到订单履约元数据 */
  fulfillmentMeta?: Record<string, unknown>;
  onClose: () => void;
  /** 已购买（履约完成）回调，由调用方决定是否解除付费墙 */
  onUnlocked?: () => void;
  /** 用户取消时是否允许关闭弹窗（默认 true） */
  allowDismiss?: boolean;
  dismissText?: string;
}

/** 简单的 hash 分流：基于 userId 选 variant */
function pickVariant(
  variants: PaywallVariant[] | null | undefined,
  userId: string | null,
): PaywallVariant | null {
  if (!variants || variants.length === 0) return null;
  const seed = userId ?? 'guest';
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) % 1_000_000;
  const total = variants.reduce((s, v) => s + (v.weight ?? 1), 0);
  const target = hash % total;
  let cum = 0;
  for (const v of variants) {
    cum += v.weight ?? 1;
    if (target < cum) return v;
  }
  return variants[0];
}

export function ReportPaywallModal({
  open,
  config,
  fulfillmentMeta,
  onClose,
  allowDismiss = true,
  dismissText = '我再考虑一下',
}: ReportPaywallProps) {
  const user = useAuthStore((s) => s.user);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const variant = useMemo(
    () => (config ? pickVariant(config.variants, user?.id ?? null) : null),
    [config, user?.id],
  );

  const copy = variant?.copy ?? config?.copy ?? null;
  const ctas = variant?.ctas ?? config?.ctas ?? null;

  if (!config) return null;

  const handleCta = async (cta: PaywallCTA) => {
    if (!user) {
      setError('请先登录后再下单');
      return;
    }
    setError(null);
    setSubmitting(cta.label);
    try {
      const order = await createOrder({
        productCode: cta.productCode,
        channel: 'alipay',
        fulfillmentMeta,
      });
      if (!submitPayment(order)) {
        setError('下单成功但未获取到支付链接');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-bg-overlay backdrop-blur-sm"
          onClick={() => allowDismiss && onClose()}
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', damping: 24, stiffness: 280 }}
            className="w-full md:max-w-lg bg-bg-elevated border-t md:border md:rounded-xl border-border-accent shadow-gold p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {copy && (
              <>
                <h2 className="font-serif text-2xl text-fg mb-2">{copy.title}</h2>
                {copy.subtitle && (
                  <p className="text-sm text-fg-1 leading-relaxed mb-5">{copy.subtitle}</p>
                )}
                {copy.hookList && copy.hookList.length > 0 && (
                  <ul className="space-y-2 mb-6">
                    {copy.hookList.map((h: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-fg-1">
                        <span className="text-accent mt-1">◆</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {error && <p className="text-sm text-danger mb-4">{error}</p>}

            <div className="space-y-2">
              {ctas?.primary && (
                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => handleCta(ctas.primary)}
                  disabled={submitting !== null}
                >
                  {submitting === ctas.primary.label ? '生成订单…' : ctas.primary.label}
                </Button>
              )}
              {ctas?.secondary && (
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full"
                  onClick={() => handleCta(ctas.secondary!)}
                  disabled={submitting !== null}
                >
                  {submitting === ctas.secondary.label ? '生成订单…' : ctas.secondary.label}
                </Button>
              )}
            </div>

            {allowDismiss && (
              <button
                onClick={onClose}
                className="block mx-auto mt-4 text-xs text-fg-muted hover:text-fg-2"
              >
                {dismissText}
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
